import styled from '@emotion/styled';
import { BaseButton } from '../base/button/button';
import { Variables } from '../base/variables/variables';

const V = Variables.ThemeLight;

/*
 * The claim drawer (FE-116, design 05) renders inside core's `SidePanel` with its `fixed below-topnav` classes, at
 * the panel's own z-index (1200) — NOT raised like the payer drawer's 1400. This drawer opens from a
 * table row, not from a MUI popover, so nothing invisible sits over it; and its actions (the worker
 * dialog, Close Out) open MUI dialogs at 1300, which must land ABOVE it. At 1400 the drawer would
 * cover its own dialogs.
 */

export const Body = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${V.Background2};
`;

export const Header = styled.header`
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 18px 20px 16px;
    border-bottom: 1px solid ${V.Border1};
    background: ${V.Background3};
`;

export const TitleRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
`;

export const TitleText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
`;

export const Title = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: ${V.TextStrong};
`;

export const Subtitle = styled.div`
    font-size: 12.5px;
    color: ${V.TextMild};
    overflow-wrap: anywhere;
`;

export const Kpis = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
`;

export const Kpi = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

export const KpiLabel = styled.span`
    font-size: 13px;
    color: ${V.TextMild};
`;

/** `pending` is a value the API cannot give: muted, with the reason on hover — never a zero. */
export const KpiValue = styled.span<{ pending?: boolean }>`
    font-size: 19px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: ${({ pending }) => (pending ? V.TextXMild : V.TextStrong)};
    cursor: ${({ pending }) => (pending ? 'help' : 'auto')};
`;

/** `min-height: 0` is what lets a flex child scroll instead of growing its parent. */
export const Scroll = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
`;

export const Block = styled.section`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 18px 20px;
    border-bottom: 1px solid ${V.Border1};

    :last-of-type {
        border-bottom: none;
    }
`;

export const Badge = styled.span`
    align-self: flex-start;
    padding: 3px 9px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: ${V.AttentionInverted3};
    background: ${V.AttentionMild2};
`;

export const Reason = styled.div`
    font-size: 14px;
    line-height: 1.55;
    color: ${V.TextStrong};
`;

export const Remark = styled.div`
    font-size: 13px;
    line-height: 1.55;
    color: ${V.TextMild};
`;

export const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
`;

export const Chip = styled.span<{ pending?: boolean }>`
    padding: 4px 9px;
    border-radius: 6px;
    font-size: 12px;
    color: ${({ pending }) => (pending ? V.TextXMild : V.TextMedium)};
    background: ${V.Background1};
    border: 1px solid ${V.Border1};
    cursor: ${({ pending }) => (pending ? 'help' : 'auto')};
`;

/** The design's Configure Worker card: the whole card is the button — core BaseButton, styled as a card. */
export const WorkerCard = styled(BaseButton)`
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 14px 16px;
    border-radius: 10px;
    border: 1px solid ${V.PrimaryInverted3};
    background: ${V.PrimaryMild2};
    color: ${V.PrimaryInverted3};
    text-align: left;
    cursor: pointer;
    font: inherit;
`;

export const WorkerText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
`;

export const WorkerTitle = styled.span`
    font-size: 15px;
    font-weight: 600;
`;

export const WorkerMeta = styled.span`
    font-size: 12.5px;
    line-height: 1.5;
    color: ${V.TextMedium};
`;

export const Eyebrow = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

/** Grid items stretch, so each button fills its cell without overriding the button's own look. */
export const Actions = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
`;

/** Wraps a disabled action so its reason still shows on hover — a disabled button gets no events. */
export const ActionSlot = styled.span`
    display: grid;
    cursor: help;
`;

export const ActionLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

export const Activity = styled.ol`
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

export const ActivityItem = styled.li`
    display: grid;
    grid-template-columns: 10px minmax(0, 1fr);
    gap: 10px;
`;

export const Dot = styled.span<{ first?: boolean }>`
    width: 8px;
    height: 8px;
    margin-top: 6px;
    border-radius: 50%;
    background: ${({ first }) => (first ? V.PrimaryInverted3 : V.Border2)};
`;

export const ActivityText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 13.5px;
    color: ${V.TextStrong};
`;

export const ActivityMeta = styled.span`
    font-size: 12px;
    color: ${V.TextMild};
`;

export const Muted = styled.div`
    font-size: 13px;
    color: ${V.TextMild};
`;
